"use client";

import { createContext, useCallback, useContext, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ToastContainer } from "@/components/ui/toast/container";

interface ToastContextValue {
  showToast: (
    message: string,
    options?: {
      type?: ToastType;
      duration?: number;
      position?: ToastPosition;
    },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (
      message: string,
      { type = "info", duration = 4000, position = "top-right" } = {},
    ) => {
      const id = uuidv4();
      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          type: type as ToastType,
          duration,
          position: position as ToastPosition,
        },
      ]);
    },
    [],
  );

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};
